import { useNavigate } from 'react-router-dom'
import { Bluetooth, ChevronRight } from 'lucide-react'
import { MovementTypeCard } from '@/components/rfid/MovementTypeCard'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { MOVEMENTS, type MovementConfig, type MovementId } from '@/config/movements'
import { BRAND } from '@/constants/brand'
import { useLocale } from '@/i18n/LocaleProvider'
import { useRfidSessionStore } from '@/stores/rfidSessionStore'
import { cn } from '@/utils/cn'

function navigateForMovement(navigate: ReturnType<typeof useNavigate>, m: MovementConfig) {
  if (m.id === 'transfer_out') {
    navigate('/transfer/customer?movement=transfer_out')
    return
  }
  if (m.id === 'transfer_in') {
    navigate('/transfer/customer?movement=transfer_in')
    return
  }
  if (m.listBacked) {
    navigate(`/movements/${m.id as MovementId}/documents`)
    return
  }
  navigate(`/transfer/customer?movement=${m.id}`)
}

export default function HomePage() {
  const navigate = useNavigate()
  const { t } = useLocale()
  const scannerConnected = useRfidSessionStore((s) => s.scannerConnected)
  const scannerPending = useRfidSessionStore((s) => s.scannerPending)
  const scannerType = useRfidSessionStore((s) => s.scannerType)
  const deviceConnected = useRfidSessionStore((s) => s.deviceConnected)

  const statusLine = scannerConnected
    ? `${deviceConnected?.rfid_device ?? scannerType ?? 'Scanner'} · ${t.Home.connectedReady}`
    : scannerPending
      ? `${deviceConnected?.rfid_device ?? scannerType ?? 'Scanner'} · waiting for first tag`
      : t.Home.tapToConnect

  return (
    <div className="rfid-web-frame">
      <RfidAppHeader title={t.App.name} subtitle={t.Home.subtitle} />

      <div className="rfid-web-body">
        <button
          type="button"
          onClick={() => navigate('/scanner')}
          className={cn(
            'mb-5 flex w-full items-center gap-3.5 rounded-2xl border-[1.5px] border-border',
            'bg-card px-4 py-4 text-left shadow-sm transition-opacity hover:opacity-95 sm:px-5'
          )}
        >
          <span
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px]"
            style={{ background: `${BRAND}14`, color: BRAND }}
          >
            <Bluetooth className="h-5 w-5" strokeWidth={2} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-black text-foreground">
              {t.Home.openConnect}
            </span>
            <span className="mt-1 flex items-center gap-2 text-xs font-semibold leading-4 text-muted-foreground">
              <span
                className={cn(
                  'inline-block h-2 w-2 shrink-0 rounded-full',
                  scannerConnected
                    ? 'bg-emerald-500'
                    : scannerPending
                      ? 'bg-amber-500'
                      : 'bg-muted-foreground'
                )}
              />
              <span className="min-w-0">{statusLine}</span>
            </span>
          </span>
          <ChevronRight className="h-5 w-5 shrink-0" style={{ color: BRAND }} strokeWidth={2.5} />
        </button>

        <div className="rfid-section-label">{t.Home.sectionTitle}</div>
        <div className="rfid-movement-grid">
          {MOVEMENTS.map((m) => (
            <MovementTypeCard
              key={m.id}
              movement={m}
              onClick={() => navigateForMovement(navigate, m)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
